import { rizaSatirDurumu, rizaSatirMetni } from "./riza-satiri";
import { useCallback, useEffect, useState } from "react";
import { Buton, Cip, Iskelet, Kart } from "@koclukpanel/ui";
import {
  ApiHatasi,
  metinGetir,
  rizaGeriCek,
  rizaVer,
  rizaDurumu,
  rizalarim,
  veriPaketim,
  type HukukiMetin,
  type RizaDurumu,
  type RizaTuru,
} from "./api";

/**
 * ONAYLARIM — KVKK rizasi ve veri paketi (F-LGL-02)
 *
 * Ogrenci uygulamasi MOBIL ONCELIKLI (CLAUDE.md): tek elle
 * kullanilabilmeli. Bu yuzden liste tek sutun, dokunma hedefleri
 * `min-h-dokunma` ve metin tam ekran aciliyor — yan panel degil.
 */

export const RIZA_ADI: Record<RizaTuru, string> = {
  PRIVACY_NOTICE: "Aydınlatma metni",
  PARENT_GUARANTEE: "Veli muvafakati",
  DISTANCE_SALES: "Mesafeli satış sözleşmesi",
  PRE_INFO_FORM: "Ön bilgilendirme formu",
  WITHDRAWAL_RIGHT: "Cayma hakkı bilgilendirmesi",
  DATA_TRANSFER_ABROAD: "Yurt dışına aktarım",
  CONTACT_SMS: "SMS ile bilgilendirme",
  CONTACT_WHATSAPP: "WhatsApp ile bilgilendirme",
  CONTACT_EMAIL: "E-posta ile bilgilendirme",
  MARKETING: "Kampanya ve tanıtım iletisi",
  PROTECTED_NOTES: "Korumalı danışmanlık notu",
};

/** Sunucudaki listenin aynisi — bkz. panel `Kvkk.tsx` icindeki not. */
const GERI_CEKILEMEZ: RizaTuru[] = [
  "PRIVACY_NOTICE",
  "DISTANCE_SALES",
  "PRE_INFO_FORM",
  "PARENT_GUARANTEE",
  "WITHDRAWAL_RIGHT",
];

interface OnaylarimProps {
  hedef?: { userId: string; ad: string };
  onDegisti?: () => void | Promise<void>;
  kilitli?: boolean;
}

export function Onaylarim(props: OnaylarimProps = {}) {
  if (props.hedef && !props.hedef.userId)
    return <p role="alert">Güncelleme bekleniyor, sayfayı yenileyin</p>;
  // Çocuk değiştiğinde önceki metin, sürüm ve devam eden seçim yeni çocuğa taşınmaz.
  return <OnaylarimIcerigi key={props.hedef?.userId ?? "kendim"} {...props} />;
}

function OnaylarimIcerigi({ hedef, onDegisti, kilitli = false }: OnaylarimProps) {
  const hedefUserId = hedef?.userId;
  const [gosterilen, setGosterilen] = useState<Partial<Record<RizaTuru, string>>>({});
  const [durum, setDurum] = useState<RizaDurumu | null>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState<string | null>(null);
  const [islemde, setIslemde] = useState<RizaTuru | null>(null);
  const [yenidenOkunacak, setYenidenOkunacak] = useState<RizaTuru | null>(null);
  const [metinYukleniyor, setMetinYukleniyor] = useState(false);
  const [acikMetin, setAcikMetin] = useState<HukukiMetin | null>(null);

  const yukle = useCallback(async (): Promise<RizaDurumu | null> => {
    setYukleniyor(true);
    setHata(null);
    try {
      const taze = await (hedefUserId ? rizaDurumu(hedefUserId) : rizalarim());
      setDurum(taze);
      return taze;
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Onaylar alınamadı.");
      return null;
    } finally {
      setYukleniyor(false);
    }
  }, [hedefUserId]);

  useEffect(() => {
    setDurum(null);
    setAcikMetin(null);
    void yukle();
  }, [yukle]);

  /** Başarılıysa taze durumu, değilse `null` döner (metin içi onay akışı sıradakini oradan seçiyor). */
  const islem = async (tur: RizaTuru, ne: "ver" | "cek"): Promise<RizaDurumu | null> => {
    if (hedef && !hedefUserId) {
      setHata("Güncelleme bekleniyor, sayfayı yenileyin");
      return null;
    }
    if (ne === "ver" && !gosterilen[tur]) return null;
    if (ne === "ver" && gosterilen[tur] !== durum?.yururluktekiSurumler[tur]) {
      setYenidenOkunacak(tur);
      await metniAc(tur);
      setHata("Metin güncellendi, yeniden okuyun ve yeniden onaylayın.");
      return null;
    }
    setIslemde(tur);
    setHata(null);
    try {
      if (ne === "ver") await rizaVer(tur, gosterilen[tur]!, hedefUserId);
      else await rizaGeriCek(tur, hedefUserId);
      const taze = await yukle();
      await onDegisti?.();
      return taze;
    } catch (err) {
      if (err instanceof ApiHatasi && err.durum === 409 && err.kod === "METIN_DEGISTI") {
        setAcikMetin(null);
        setYenidenOkunacak(tur);
        await yukle();
        await metniAc(tur);
        setHata("Metin güncellendi, yeniden okuyun ve yeniden onaylayın.");
      } else setHata(err instanceof ApiHatasi ? err.message : "İşlem tamamlanamadı.");
      return null;
    } finally {
      setIslemde(null);
    }
  };

  /*
    ================================================================
    METİN EKRANINDA ONAY — UI/UX Dalga 2 (M9, karar D2)
    ================================================================
    Eskiden bir onay üç dokunuştu: "Oku" → "← Geri" → "Onaylıyorum"; beş zorunlu
    onayda on beş. Şimdi onay düğmesi METNİN SONUNDA ("Okudum, onaylıyorum") ve
    onaydan sonra sıradaki ZORUNLU onayın metni kendiliğinden açılıyor: beş onay
    = "Oku" + beş onay = altı dokunuş.

    OKUMA ZORUNLULUĞU KORUNUYOR (F-LGL-03a): onay yalnız metin AÇILMIŞKEN
    verilebiliyor (`gosterilen[tur]` hâlâ metnin sürümünü taşıyor ve `islem` onu
    sunucunun güncel sürümüyle karşılaştırıyor), düğmeye ulaşmak metnin sonuna
    inmeyi gerektiriyor. Toplu onay YOK, okumadan onay YOK; "okundu"nun tanımı
    değişmedi, yalnız düğmenin yeri taşındı.
  */
  const metindenOnayla = async (tur: RizaTuru) => {
    const taze = await islem(tur, "ver");
    if (!taze) return;
    const siradaki = taze.engelleyenEksikler[0];
    if (siradaki && kendiVerirTaze(taze)) {
      await metniAc(siradaki);
    } else {
      setAcikMetin(null);
    }
  };
  const kendiVerirTaze = (d: RizaDurumu): boolean =>
    hedef ? d.rizayiKimVerir === "VELI" : d.rizayiKimVerir === "KENDISI";

  const metniAc = async (tur: RizaTuru) => {
    setHata(null);
    setMetinYukleniyor(true);
    try {
      const metin = await metinGetir(tur);
      setDurum((eski) =>
        eski
          ? {
              ...eski,
              yururluktekiSurumler: { ...eski.yururluktekiSurumler, [tur]: metin.version },
              rizalar: eski.rizalar.map((r) =>
                r.type === tur && eski.yururluktekiSurumler[tur] !== metin.version
                  ? { ...r, guncelMi: false }
                  : r,
              ),
            }
          : eski,
      );
      setAcikMetin(metin);
      setGosterilen((eski) => ({ ...eski, [tur]: metin.version }));
      setYenidenOkunacak((eski) => (eski === tur ? null : eski));
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Metin alınamadı.");
    } finally {
      setMetinYukleniyor(false);
    }
  };

  if (yukleniyor && !durum) {
    return <Iskelet satir={4} />;
  }

  if (hata && !durum) {
    return (
      <div className="flex flex-col gap-3 p-4">
        <p role="alert" className="text-hata-500 text-sm">
          {hata}
        </p>
        <Buton gorunum="ikincil" onClick={() => void yukle()}>
          Tekrar dene
        </Buton>
      </div>
    );
  }

  if (!durum) return null;

  /*
    METIN TAM EKRAN aciliyor, yan panel degil: telefonda yan panel
    metnin yarisini gosterir ve okumadan onaylamaya iter. Onay
    gecerliligi metnin OKUNABILMIS olmasina bagli.
  */
  const acikTur = acikMetin?.type;
  const metneOnayVerilebilir =
    acikTur !== undefined &&
    (hedef ? durum.rizayiKimVerir === "VELI" : durum.rizayiKimVerir === "KENDISI") &&
    durum.rizalar.find((r) => r.type === acikTur)?.guncelMi !== true;

  if (acikMetin) {
    return (
      <div className="flex flex-col gap-4 p-4">
        <Buton gorunum="sessiz" onClick={() => setAcikMetin(null)}>
          ← Geri
        </Buton>
        {hata && <p role="alert">{hata}</p>}
        <h1 className="text-notr-900 text-lg font-semibold">{acikMetin.title}</h1>
        <p className="text-notr-600 text-xs">Sürüm {acikMetin.version}</p>
        <p className="text-notr-700 text-sm leading-relaxed whitespace-pre-wrap">
          {acikMetin.body}
        </p>
        {metneOnayVerilebilir && (
          <div className="border-notr-200 flex flex-col gap-2 border-t pt-4">
            {durum.engelleyenEksikler.length > 1 && (
              <p className="text-notr-600 text-xs">
                Bu onaydan sonra {durum.engelleyenEksikler.length - 1} zorunlu onay daha var;
                sıradaki metin kendiliğinden açılır.
              </p>
            )}
            <Buton
              onClick={() => void metindenOnayla(acikMetin.type)}
              disabled={islemde !== null || yukleniyor || metinYukleniyor}
            >
              {islemde === acikMetin.type ? "Kaydediliyor…" : "Okudum, onaylıyorum"}
            </Buton>
          </div>
        )}
      </div>
    );
  }

  const turler = Object.keys(durum.yururluktekiSurumler) as RizaTuru[];
  const verilen = new Map(durum.rizalar.map((r) => [r.type, r]));
  const kendiVerir = hedef ? durum.rizayiKimVerir === "VELI" : durum.rizayiKimVerir === "KENDISI";
  const engelleyen = new Set(durum.engelleyenEksikler);
  /* `eksikIstegeBagli` bu ekranda HIC okunmuyordu — gerekcesi `riza-satiri.ts`te. */
  const istegeBagli = new Set(durum.eksikIstegeBagli);

  return (
    <div className="flex flex-col gap-4 p-4">
      <h1 className="text-notr-900 text-lg font-semibold">
        {hedef ? `${hedef.ad} — Çocuğumun onayları` : "Onaylarım"}
      </h1>

      {/*
        Yas kurali SUNUCUDAN. Ekran hesap yapmiyor — yapsaydi saati
        yanlis olan bir telefonda 18'ini yeni dolduran ogrenci hala
        "velin versin" gorurdu.
      */}
      {!kendiVerir && (
        <p className="rounded-alan bg-uyari-500/15 text-notr-800 p-3 text-sm">
          {hedef
            ? "Çocuğunuz reşit olduğu için onaylarını yalnızca kendisi verebilir. Burada yalnızca görüntüleyebilirsiniz."
            : "18 yaşından küçük olduğun için onayları velin veriyor. Burada hangilerinin verildiğini görebilirsin."}
        </p>
      )}

      {durum.engelleyenEksikler.length > 0 && (
        <div role="alert" className="rounded-alan bg-hata-500/15 text-notr-800 p-3 text-sm">
          <strong>Eksik onay var:</strong>
          <ul className="mt-1 list-inside list-disc">
            {durum.engelleyenEksikler.map((t) => (
              <li key={t}>{RIZA_ADI[t]}</li>
            ))}
          </ul>
        </div>
      )}

      {hata && (
        <p role="alert" className="text-hata-500 text-sm">
          {hata}
        </p>
      )}

      {turler.length === 0 ? (
        <p className="text-notr-600 text-sm">Onaylanacak bir metin yok.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {turler.map((tur) => {
            const kayit = verilen.get(tur);
            const gecerli = kayit?.guncelMi === true;
            const cekilebilir = gecerli && !GERI_CEKILEMEZ.includes(tur);
            const satirDurumu = rizaSatirDurumu({
              gecerli,
              kayitVar: kayit !== undefined,
              engelliyorMu: engelleyen.has(tur),
              istegeBagliMi: istegeBagli.has(tur),
            });

            return (
              <Kart key={tur} olarak="li">
                <div className="flex items-start gap-2">
                  <span className="min-w-0 flex-1">
                    <span className="text-notr-900 block text-[15px] font-semibold">
                      {RIZA_ADI[tur]}
                      <span className="block text-xs">Sürüm {durum.yururluktekiSurumler[tur]}</span>
                    </span>
                    <span className="text-notr-600 mt-0.5 block text-[13px]">
                      {gecerli ? (
                        <>Onaylandı{kayit!.veliVerdi ? " — velin onayladı" : ""}</>
                      ) : (
                        /*
                          UC AYRI "VERILMEDI" HALI, UC AYRI CUMLE.

                          Once ikisi ayni cumleyle ("Henuz
                          onaylanmadi") gorunuyordu: hesabi durduran
                          onay ile istege bagli onay ayirt
                          EDILEMIYORDU. Sunucu ikisini AYRI listelerde
                          gonderiyor; ekran o ayrimi dusuruyordu.

                          Eski surume verilmis onay da ayri: ogrenci
                          "ben bunu zaten onaylamistim" diye dusunur,
                          yeniden onaylamaz ve sistem onu rizasiz
                          sayar.
                        */
                        <span
                          className={
                            satirDurumu === "eksik-istege-bagli"
                              ? "text-notr-600"
                              : "text-uyari-600"
                          }
                        >
                          {rizaSatirMetni(satirDurumu)}
                        </span>
                      )}
                    </span>
                  </span>

                  {/*
                    DUZELTME 04.09.2026: once `Rozet`e cevirmistim ve
                    YANLISTI. Teslim notunun "rozet radius-rozet tasir"
                    ozetine bakip uygulamistim; artboard'a bakinca
                    `Onaylarim.dc.html`in durum cipi `radius-alan`
                    cikti. Uyusmazlikta CIZIM kazanir.
                  */}
                  <span className="shrink-0">
                    <Cip ton={gecerli ? "onay" : "notr"}>{gecerli ? "Onaylı" : "Onaysız"}</Cip>
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {/*
                    "Oku" HER ZAMAN var — onaylasa da onaylamasa da,
                    verse de velisi verse de. Onayladigi metni bir
                    daha okuyamamak, onayin kendisini anlamsizlastirir.
                  */}
                  <Buton
                    gorunum="ikincil"
                    onClick={() => void metniAc(tur)}
                    disabled={metinYukleniyor || islemde !== null}
                  >
                    Oku
                  </Buton>

                  {kendiVerir && !gecerli && (
                    <Buton
                      onClick={() => void islem(tur, "ver")}
                      disabled={
                        islemde !== null ||
                        yukleniyor ||
                        metinYukleniyor ||
                        yenidenOkunacak === tur ||
                        !gosterilen[tur]
                      }
                      aria-describedby={!gosterilen[tur] ? `onay-ipucu-${tur}` : undefined}
                    >
                      {islemde === tur ? "Kaydediliyor…" : "Onaylıyorum"}
                    </Buton>
                  )}
                  {/*
                    PASİF DÜĞMENİN SEBEBİ YAZIYOR — UI/UX Dalga 1 (M-06).
                    Okumadan onay verilemiyor (kural doğru), ama kilitli
                    ekrandaki veli/öğrenci soluk "Onaylıyorum"a basıp hiçbir
                    şey olmayınca uygulamanın bozuk olduğunu sanıyordu.
                    `aria-describedby`: ekran okuyucu da "devre dışı"nın
                    nedenini duysun.
                  */}
                  {kendiVerir && !gecerli && !gosterilen[tur] && (
                    <span id={`onay-ipucu-${tur}`} className="text-notr-600 self-center text-xs">
                      {hedef ? "Önce metni okuyun" : "Önce metni oku"}
                    </span>
                  )}

                  {kendiVerir && cekilebilir && (
                    <Buton
                      gorunum="sessiz"
                      onClick={() => void islem(tur, "cek")}
                      disabled={
                        islemde !== null || yukleniyor || metinYukleniyor || yenidenOkunacak === tur
                      }
                    >
                      Geri çek
                    </Buton>
                  )}

                  {kendiVerir && gecerli && !cekilebilir && (
                    <span className="text-notr-600 self-center text-xs">Geri çekilemez</span>
                  )}
                </div>
              </Kart>
            );
          })}
        </ul>
      )}

      {!hedef && !kilitli && <VeriPaketiBolumu />}
    </div>
  );
}

/**
 * ================================================================
 * VERI PAKETI — KVKK m.11 HAKKI
 * ================================================================
 * Ogrenci kendi verisini indirebiliyor. Bu bir "ozellik" degil,
 * kanuni hak — bu yuzden izne baglanmiyor ve ekranda saklanmiyor.
 *
 * Neyin PAKETE GIRMEDIGI de yaziliyor: yazilmasaydi ogrenci "eksik
 * gonderdiler" diye dusunurdu. Oysa eksik degil, hukuki gereklilik.
 */
export function VeriPaketiBolumu() {
  const [indiriliyor, setIndiriliyor] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [bitti, setBitti] = useState(false);

  const indir = async () => {
    setIndiriliyor(true);
    setHata(null);
    setBitti(false);
    try {
      const paket = await veriPaketim();
      const metin = JSON.stringify(paket, null, 2);
      const url = URL.createObjectURL(new Blob([metin], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `verilerim-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setBitti(true);
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : "Paket alınamadı.");
    } finally {
      setIndiriliyor(false);
    }
  };

  return (
    <Kart className="mt-2 flex flex-col gap-3">
      <h2 className="text-notr-900 text-sm font-semibold">Verilerimi indir</h2>
      <p className="text-notr-600 text-sm">
        Sistemdeki verilerinin bir kopyasını makine okunur (JSON) biçimde alabilirsin: kişisel
        bilgiler, programlar, çalışma kayıtların, deneme sonuçların, veli raporların ve sana açık
        görüşme özetleri.
      </p>
      <p className="text-notr-600 text-xs">
        Uzmanının kendine yazdığı çalışma notları ve korumalı danışmanlık notları
        <strong> bu pakete dahil edilmez</strong> — hukuki bir gerekliliktir, eksiklik değildir.
      </p>

      <div>
        <Buton onClick={() => void indir()} disabled={indiriliyor}>
          {indiriliyor ? "Hazırlanıyor…" : "Verilerimi indir"}
        </Buton>
      </div>

      {bitti && (
        <p role="status" className="text-onay-700 text-sm">
          Paket indirildi.
        </p>
      )}
      {hata && (
        <p role="alert" className="text-hata-500 text-sm">
          {hata}
        </p>
      )}
    </Kart>
  );
}
